import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { APP } from 'src/app/config/app';
import { IAvailabilities, IFilterPanelSelection, IFilterPanelValues } from 'src/app/models';
import { ExpeditionStatusGroup, FilterField, FilterType, UserRole } from 'src/app/models/enums';
import { IBulkActionType } from 'src/app/models/enums/bulkActionType';
import { LiteralService, StorageService, UtilsService } from 'src/app/services';
import { images } from 'src/images';
import { ActivatedRoute } from '@angular/router';
import { IAvailabilitiesFilters, deserializeAvailabilitiesFilters } from 'src/app/filters';
import { FILTER_PANEL_ADAPTERS } from 'src/app/shared/filter-panel/filterPanelAdapters';

@Component({
  selector: 'app-availabilities-search-bar',
  templateUrl: './availabilities-search-bar.component.html',
  styleUrl: './availabilities-search-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AvailabilitiesSearchBarComponent
  implements OnChanges, OnInit, OnDestroy
{
  @Input() placeholder = '';
  @Input() embedded = false;
  @Input() searching: boolean = false;
  @Input() searchValue = '';
  @Input() availabilities: IAvailabilities[] = [];
  @Input() setupUrl = '';

  private queryParamsSub?: Subscription;
  @Output() filter = new EventEmitter();
  @Output() searchEventEmitter = new EventEmitter<string>();
  @Output() enableMultiselect = new EventEmitter<string>();
  @Output() onBulkAction = new EventEmitter();
  @Output() onChangePaginationSize = new EventEmitter<number>();
  @Output() onCreateDepartures = new EventEmitter<void>();

  public filterOpened = false;
  public filtersApplied = 0;
  public allowBulkEditing = true;
  public filterTypes = FilterType;
  public filterFields: FilterField[] = [
    FilterField.DATE_RANGE,
    FilterField.TIME_RANGE,
    FilterField.EXPEDITION_STATUS,
    FilterField.BOOKINGS,
  ];
  public panelSelection: IFilterPanelSelection | null = null;

  public bulkActions = [
    {
      action: IBulkActionType.EDIT,
      label: 'availabilities.actions.edit.title',
      image: images.booking.actions.bulkSelect,
    },
    {
      action: IBulkActionType.DELETE,
      label: 'availabilities.actions.delete.title',
      image: images.booking.actions.delete,
    },
  ];

  public bulkSelectedAction = '';

  public images = images;

  public adminRole: UserRole;

  public rowsDisplayOpened = false;
  public rowsDisplayValue = APP.DEFAULT_PAGE_SIZE;
  public defaultRowsDisplay = APP.DEFAULT_PAGE_SIZE;

  public routeId: any;

  constructor(
    public literalService: LiteralService,
    private activatedRoute: ActivatedRoute,
    private storageService: StorageService,
    private utilsService: UtilsService,
  ) {}

  search(event: any) {
    this.searchEventEmitter.emit(event);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searching'] && changes['searching'].currentValue) {
      this.bulkSelectedAction = '';
      this.enableMultiselect.emit('');
    }
  }

  ngOnInit() {
    this.applyFromUrl();
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe(
      (params) => {
        this.routeId = params.get('routeId');
        this.applyFromUrl();
      },
    );
  }

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
  }

  private applyFromUrl() {
    const params = this.activatedRoute.snapshot.queryParamMap;
    const sizeParam = params.get('size');
    const sizeFromUrl = sizeParam ? Number(sizeParam) : null;
    this.rowsDisplayValue = (sizeFromUrl && !isNaN(sizeFromUrl) && sizeFromUrl > 0)
      ? sizeFromUrl
      : this.defaultRowsDisplay;
    const filters = deserializeAvailabilitiesFilters(params);
    this.panelSelection = FILTER_PANEL_ADAPTERS[FilterType.AVAILABILITIES]!.toSelection(filters);
    this.filtersApplied = this.utilsService.getFiltersCount({
      status: this.isDefaultStatus(filters) ? [] : filters.selectedStatus,
      bookings: filters.bookings !== undefined,
      dateRange: !!(filters.fromDate || filters.toDate),
      timeRange: !!(filters.startTime || filters.endTime),
    });
  }

  private isDefaultStatus(filters: IAvailabilitiesFilters): boolean {
    const pending = APP.EXPEDITION_STATUS_GROUPS[FilterType.AVAILABILITIES]?.[ExpeditionStatusGroup.PENDING]?.statuses ?? [];
    const selected: string[] = filters.selectedStatus;
    return selected.length === pending.length && pending.every((status) => selected.includes(status));
  }

  onActionChange(action: string) {
    this.bulkSelectedAction = action;
    this.enableMultiselect.emit(action);
  }

  updateNumberOfElements(event: number) {
    this.rowsDisplayOpened = false;
    this.rowsDisplayValue = event > 0 ? event : this.defaultRowsDisplay;
    this.storageService.setAvailabilitiesPageSize(this.rowsDisplayValue);
    this.onChangePaginationSize.emit(event);
  }

  showActionButton(action: string) {
    return (
      this.bulkSelectedAction === action &&
      this.availabilities.filter((availability) => availability.selected)
        .length > 0
    );
  }

  applyFilter(values: IFilterPanelValues) {
    this.filter.emit(FILTER_PANEL_ADAPTERS[FilterType.AVAILABILITIES]!.toFilters(values));
    this.filterOpened = false;
  }
}
